import React, { useState } from "react";
import classNames from "classnames";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { TransactionListItemDto } from "../../common/types/transaction";
import "./TransactionAction.scss";
import { RequiredAction } from "../../common/requiredActions";
import { CheckIcon } from "../../icons";
import { getAmountSaved } from "../../helpers/transaction";
import { Amount } from "../general/Amount/Amount";
import { TransactionActionsRequiredNumber } from "./TransactionActionsRequiredNumber";

interface Props {
    transaction: TransactionListItemDto;
    visibleRequiredActions?: RequiredAction[];
}

function describeRequiredAction(action: RequiredAction): string {
    if (action === "CATEGORIZE") {
        return "Category required";
    }
    if (action === "TRANSFER_MATCH") {
        return "Transfer needs a match";
    }
    if (action === "UPLOAD_RECEIPT") {
        return "Receipt required";
    }
    if (action === "ADD_MEMO") {
        return "Memo required";
    }
    if (action.startsWith("ANSWER_QUESTION:")) {
        return "Additional tax information required";
    }
    return "Complete a required action";
}

export const TransactionAction: React.FC<Props> = ({
    transaction,
    visibleRequiredActions,
}) => {
    let outstandingActions =
        transaction.requiredActions?.filter(
            (action) =>
                !visibleRequiredActions ||
                visibleRequiredActions.includes(action),
        ) ?? [];
    outstandingActions = Array.from(new Set(outstandingActions));
    const amountSaved = getAmountSaved(transaction);
    const hasSavings = Boolean(amountSaved);
    const [showTooltip, setShowTooltip] = useState(false);
    const tooltipEnabled = outstandingActions.length > 0 || hasSavings;

    let tooltipContent: React.ReactNode = null;
    if (outstandingActions.length > 0) {
        const descriptions = outstandingActions.map(describeRequiredAction);
        const uniqueDescriptions = Array.from(new Set(descriptions));
        tooltipContent = (
            <div>
                {uniqueDescriptions.map((desc) => (
                    <div key={desc}>{desc}</div>
                ))}
            </div>
        );
    } else if (hasSavings) {
        tooltipContent = (
            <div className="transaction-action__savings">
                <span>Tax savings</span>
                <span>
                    <Amount amount={amountSaved ?? 0} showPositive />
                </span>
            </div>
        );
    }

    const getStatusIcon = () => {
        if (outstandingActions.length) {
            return (
                <TransactionActionsRequiredNumber
                    value={outstandingActions.length}
                />
            );
        } else if (hasSavings) {
            return <CheckIcon className="icon-color-blue-600" />;
        } else {
            return "•";
        }
    };

    return (
        <OverlayTrigger
            placement="top"
            onToggle={setShowTooltip}
            show={showTooltip && tooltipEnabled}
            overlay={
                <Tooltip id={`actions-${transaction.id}`}>
                    {tooltipContent}
                </Tooltip>
            }
        >
            <span
                className={classNames(
                    `transaction-action transaction-action--${transaction.status}`,
                    {
                        "transaction-action--has-savings": hasSavings,
                    },
                )}
                data-testid={`transaction-action${hasSavings ? "-has-savings" : ""}`}
            >
                {getStatusIcon()}
            </span>
        </OverlayTrigger>
    );
};
